import { computed, observable, action } from "mobx";
import { NetworkEvent } from "../../lib/Events";
import { describeEncodedSearchTermUsingFunctionNotationHtml } from "../../lib/Search/EncodedSearchTerm";
import { EventSearchResult, EventSearchResults, HasSearchResults, ContentSourceSearchResult, EventSearchResultsOrEventIndexOnly, EventSearchResultKey } from "../../lib/searchNetworkEvent";
import { getRegisteredDomain, urlToDomain } from "../../lib/utils/domains";
import { mapGetOrCreate } from "../../lib/utils/mapGetOrCreate";
import { DbRecordedEvent, OptionallyObservableMap } from "../../State/EvidenceDB/DatabaseTypes";
import { ProcessInfo } from "../../State/ProcessInfo";
import { LicensePermissions } from "../../State/EvidenceDB/LicensePermissions";


export type SearchTermsFound = SearchResultsObserver["searchResultsBySourceRegisteredDomain"][number]["destinations"][number]["searchTerms"];
export type EncodingResults = SearchTermsFound[number]["encodings"][number];

export type HasHarEntry = { entry: HARFormatEntry; };
export type HasDbRecordedNetworkEvent = { event: DbRecordedEvent<NetworkEvent>; };

export interface ResultsForEncoding {
	encodingString: string;
	eventsAndResults: OptionallyObservableMap<EventSearchResultKey, EventSearchResult>;
	inferredTypeNames: OptionallyObservableMap<string /* inferredTypeName */, Set<number /* eventIndex */>>;
}

export interface ResultsForSearchTermSpecified {
	searchTermAsSpecified: string;
	encodings: ResultsForEncoding[];
	inferredTypeNames: OptionallyObservableMap<string /* inferredTypeName */, Set<number /* eventIndex */>>;
}

export interface ResultsForPageDomainCodeUrlAndDestinationDomain {
	destinationDomain: string;
	bySearchTerm: ResultsForSearchTermSpecified[];
	inferredTypeNames: OptionallyObservableMap<string /* inferredTypeName */, Set<number /* eventIndex */>>;
};

export interface ResultsForPageDomainAndCodeUrl {
	codeUrl: string;
	byDestinationDomain: ResultsForPageDomainCodeUrlAndDestinationDomain[];
	inferredTypeNames: OptionallyObservableMap<string /* inferredTypeName */, Set<number /* eventIndex */>>;
}

export interface ResultsForPageDomain {
	pageDomain: string;
	byCodeUrl: OptionallyObservableMap<string, ResultsForPageDomainAndCodeUrl>;
	inferredTypeNames: OptionallyObservableMap<string /* inferredTypeName */, Set<number /* eventIndex */>>;
}

export const filterHasSearchResultsArray = <T extends HasSearchResults>(
	hasSearchResultsArray: T[],
	filter: ( searchResult: ContentSourceSearchResult ) => boolean,
): T[] => {
	const filtered = [] as T[];
	for (const item of hasSearchResultsArray) {
		const searchResults = item.searchResults.filter( s => filter(s));
		if (searchResults.length > 0) {
			filtered.push({...item, searchResults} satisfies T);
		}
	}
	return filtered;
}

const binarySearchArrayItemsByStringOrNumberField = <
	KEY extends string,
	OBJ extends Record<KEY, string | number>
>(
	arr: OBJ[],
	key: KEY,
	keyValueToFind: OBJ[KEY]
): {position: number, value: OBJ | undefined} => {
	let [lowerBound, upperBound] = [0, arr.length === 0 ? 0 : arr.length];
	while (true) {
		const position = Math.floor((upperBound + lowerBound) / 2);
		const objAtPosition = arr[position];
		if (position === upperBound || objAtPosition == null) {
			return {
				position,
				value: objAtPosition?.[key] == keyValueToFind ? objAtPosition : undefined
			};
		}
		const fieldValueAtPosition: OBJ[KEY] = objAtPosition[key];
		if (fieldValueAtPosition == keyValueToFind) {
			return {position, value: objAtPosition};
		} else if (fieldValueAtPosition < keyValueToFind) {
			lowerBound = position + 1;
		} else {
			upperBound = position;
		}
	}
}

// const updateSearchResultsBySearchTermFn = (licensePermissions: LicensePermissions, searchResultsBySearchTerm: SearchResultsBySearchTerm[] = observable.array<SearchResultsBySearchTerm>()) =>
// 	(eventOrEntry: HasSearchResultsWithNetworkRequestInformation) => {
// 		const { searchResults, ...networkRequestInformation } = eventOrEntry;
// 		const { originUrl, destinationUrl, codeUrls } = networkRequestInformation;
// 		const hideDetails = !licensePermissions.mayGenerateDetailedEvidenceAt({pageUrl: originUrl});
// 		const sourceRegisteredDomain = getRegisteredDomain(originUrl);
// 		const destinationRegisteredDomain = getRegisteredDomain(destinationUrl);
// 		if (sourceRegisteredDomain == null || destinationRegisteredDomain == null) return;
// 		for (const r of searchResults) {
// 			const {inferredDataTypeName, searchTermAsSpecified} = r;
// 			const encodingString = describeEncodedSearchTermUsingFunctionNotationHtml({...r, hideDetails});
// 			addOrUpdateArrayItemWithFieldName(searchResultsBySearchTerm, 'searchTerm', searchTermAsSpecified, 
// 				() => ({searchTerm: searchTermAsSpecified, inferredTypeNames: observable.map(), destinations: observable.array()}),
// 				(({ destinations, inferredTypeNames: inferredTypeNamesForSearchTerm }) => {
// 					addOrUpdateArrayItemWithFieldName(destinations, 'destinationRegisteredDomain', destinationRegisteredDomain,
// 						() => ({destinationRegisteredDomain, inferredTypeNames: observable.map(), sourcePages: observable.array()}),
// 						(({ sourcePages, inferredTypeNames: inferredTypeNamesForDestinationRegisteredDomain }) => {
// 							addOrUpdateArrayItemWithFieldName(sourcePages, 'sourceRegisteredDomain', sourceRegisteredDomain,
// 								() => ({sourceRegisteredDomain, encodings: observable.array(), inferredTypeNames: observable.map()}),
// 								(({ encodings, inferredTypeNames }) => {									
// 										addOrUpdateArrayItemWithFieldName(encodings, 'encodingString', encodingString, () => ({ encodingString, eventsAndResults: observable.map(), inferredTypeNames: observable.map() }),
// 											(({ eventsAndResults, inferredTypeNames }) => {
// 												if (inferredDataTypeName != null) {
// 													addToSortedArrayItems(inferredTypeNamesForSearchTerm, inferredDataTypeName);
// 													addToSortedArrayItems(inferredTypeNamesForDestinationRegisteredDomain, inferredDataTypeName);
// 													addToSortedArrayItems(inferredTypeNames, inferredDataTypeName);
// 												}
// 												eventsAndResults.push({ searchResult: r, ...networkRequestInformation });
// 											})
// 										);
// 									})
// 								);
// 							})
// 						)
// 					}
// 				)
// 			);
// 		}
// 	}

const addOrUpdateArrayItemWithFieldName = <
	KEY extends string,
	OBJ extends Record<KEY, string | number>
>(
	arr: OBJ[],
	key: KEY,
	keyValueToFind: OBJ[KEY],
	createEmptyObjIfNotFound: (keyValueToFind: OBJ[KEY]) => OBJ,
	updateObj: (obj: OBJ) => void
): OBJ[] => {
	// const before = [...arr];
	const { position, value } = binarySearchArrayItemsByStringOrNumberField(arr, key, keyValueToFind);
	if (value == null) {
		const newObj = createEmptyObjIfNotFound(keyValueToFind);
		arr.splice(position, 0, newObj);
		updateObj(newObj);
		return arr;
	} else {
		updateObj(value);
		return arr;
	}
}

const updateOrDeleteArrayItemWithFieldName = <
	KEY extends string,
	OBJ extends Record<KEY, string | number>
>(
	arr: OBJ[],
	key: KEY,
	keyValueToFind: OBJ[KEY],
	updateObjReturnTrueToDelete: (obj: OBJ) => boolean
) => {
	// const before = [...arr];
	const { position, value } = binarySearchArrayItemsByStringOrNumberField(arr, key, keyValueToFind);
	if (value == null) {
		return;
	} else {
		const shouldDelete = updateObjReturnTrueToDelete(value);
		if (shouldDelete) {
			arr.splice(position, 1);
		}
		return arr;
	}
}

const deleteSetMemberOrWholeSetIfEmpty = <
	KEY extends string
>(
	map: Map<KEY, Set<number>> | OptionallyObservableMap<KEY, Set<number>>,
	key: KEY,
	value: number,
) => {
	map.get(key)?.delete(value);
	if (map.get(key)?.size === 0) {
		map.delete(key);
	}	
}


export type ByPageDomainAndCodeUrlAndDestinationDomain = OptionallyObservableMap<string /* page domain */, ResultsForPageDomain>;

export interface SearchResultsBySourceRegisteredDomain {
    sourceRegisteredDomain: string;
    destinations: {
			destinationRegisteredDomain: string;
			searchTerms: {
					searchTermAsSpecified: string;
					inferredTypeNames: OptionallyObservableMap<string /* inferredTypeName */, Set<number /* eventIndex */>>;
					encodings: {
							encodingString: string;
							eventsAndResults: OptionallyObservableMap<EventSearchResultKey, EventSearchResult>;
							inferredTypeNames: OptionallyObservableMap<string /* inferredTypeName */, Set<number /* eventIndex */>>;
					}[];
			}[];
    }[];
}

export interface SearchResultsByDestinationDomain {
	destinationRegisteredDomain: string;
	sources: {
		sourceRegisteredDomain: string;
		searchTerms: {
			searchTermAsSpecified: string;
			inferredTypeNames: OptionallyObservableMap<string /* inferredTypeName */, Set<number /* eventIndex */>>;
			encodings: {
				encodingString: string;
				eventsAndResults: OptionallyObservableMap<EventSearchResultKey, EventSearchResult>;
				inferredTypeNames: OptionallyObservableMap<string /* inferredTypeName */, Set<number /* eventIndex */>>;
			}[];
		}[];
		inferredTypeNames: OptionallyObservableMap<string /* inferredTypeName */, Set<number /* eventIndex */>>;
	}[];
	inferredTypeNames: OptionallyObservableMap<string /* inferredTypeName */, Set<number /* eventIndex */>>;
}

export interface SearchResultsBySearchTerm {
	searchTerm: string;
	destinations: {
		destinationRegisteredDomain: string;
		sourcePages: {
			sourceRegisteredDomain: string;
			encodings: {
					encodingString: string;
					eventsAndResults: OptionallyObservableMap<EventSearchResultKey, EventSearchResult>;
					inferredTypeNames: OptionallyObservableMap<string /* inferredTypeName */, Set<number /* eventIndex */>>;
			}[];
			inferredTypeNames: OptionallyObservableMap<string /* inferredTypeName */, Set<number /* eventIndex */>>;
		}[];
		inferredTypeNames: OptionallyObservableMap<string /* inferredTypeName */, Set<number /* eventIndex */>>;
	}[];
	inferredTypeNames: OptionallyObservableMap<string /* inferredTypeName */, Set<number /* eventIndex */>>;
}



export class SearchResultsObserver {
	protected requestIdsToCodeUrls = new Map<string, string[]>();

	private _eventIndexToSearchResult = observable.map<number, EventSearchResults>();

	protected _byPageDomainAndCodeUrlAndDestinationDomain: ByPageDomainAndCodeUrlAndDestinationDomain = observable.map();
	@computed get byPageDomainAndCodeUrlAndDestinationDomain() {
		return this._byPageDomainAndCodeUrlAndDestinationDomain;
	}

	protected _searchResultsBySearchTerm = observable.array<SearchResultsBySearchTerm>();
	@computed get searchResultsBySearchTerm() {
		return this._searchResultsBySearchTerm;
	}

	protected _searchResultsBySourceRegisteredDomain = observable.array<SearchResultsBySourceRegisteredDomain>();
	@computed get searchResultsBySourceRegisteredDomain() {
		return this._searchResultsBySourceRegisteredDomain;
	}

	protected _searchResultsByDestinationDomain = observable.array<SearchResultsByDestinationDomain>();
	@computed get searchResultsByDestinationDomain() {
		return this._searchResultsByDestinationDomain;
	}

	@computed get hasSearchResultsWithNetworkRequestInformation()	: EventSearchResults[] {
		return [...this._eventIndexToSearchResult.values()]
			.filter( e => EventSearchResults(e) )
			.sort( (a, b) => a.eventIndex - b.eventIndex );
	}

	@computed get searchResultsForIpSearchTerms() {
		return filterHasSearchResultsArray(this.hasSearchResultsWithNetworkRequestInformation, (s) => s.originalDataType.startsWith('ip'));
	}

	constructor(
		protected readonly licensePermissions: LicensePermissions,
		eventsWithSearchResults: EventSearchResultsOrEventIndexOnly[] = []
	) {
		this.update(eventsWithSearchResults);
	};

	clear = action(() => {
		this._eventIndexToSearchResult.clear();
		this._searchResultsBySearchTerm.clear();
		this._searchResultsBySourceRegisteredDomain.clear();
		this._byPageDomainAndCodeUrlAndDestinationDomain.clear();
		this._searchResultsByDestinationDomain.clear();
		ProcessInfo.log(`SearchResultsObserver.clear ${this._eventIndexToSearchResult.size}`);
	});

	// onChangeFilter = () => {
	// 	this._searchResultsBySearchTerm.clear();
	// 	this._searchResultsBySourceRegisteredDomain.clear();
	// 	this._byPageDomainAndCodeUrlAndDestinationDomain.clear();
	// 	this._searchResultsByDestinationDomain.clear();
	// 	// FIXME -- re-process all unfiltered results
	// }

	update = action( (eventsWithSearchResults: EventSearchResultsOrEventIndexOnly[]) => {
		const searchResultsAdded: EventSearchResults[] = [];
		const searchResultsRemoved: EventSearchResults[] = [];
		for (const eventWithSearchResults of eventsWithSearchResults) {
			const {eventIndex} = eventWithSearchResults;
			if (EventSearchResults(eventWithSearchResults)) {
				this._eventIndexToSearchResult.set(eventIndex, eventWithSearchResults);
				searchResultsAdded.push(eventWithSearchResults);
			} else {
				const resultRemoved = this._eventIndexToSearchResult.get(eventIndex);
				if (resultRemoved != null) {
					searchResultsRemoved.push(resultRemoved);
				}
				this._eventIndexToSearchResult.delete(eventIndex);
			}
		}
		ProcessInfo.log(`SearchResultsObserver.update ${eventsWithSearchResults.length} changes, ${searchResultsAdded.length} added, ${searchResultsRemoved.length} removed`);
		
		for (const resultToRemove of searchResultsRemoved) {
			this.deleteSearchResultsBySearchTerm(resultToRemove);
			this.deleteSearchResultsBySourceRegisteredDomain(resultToRemove);
			this.deleteSearchResultsByDestinationDomain(resultToRemove);
			this.deleteByPageDomainAndCodeUrlAndDestinationDomain(resultToRemove);
		}
		for (const resultToAdd of searchResultsAdded) {
			this.addSearchResultsBySearchTerm(resultToAdd);
			this.addSearchResultsBySourceRegisteredDomain(resultToAdd);
			this.addSearchResultsByDestinationDomain(resultToAdd);
			this.addByPageDomainAndCodeUrlAndDestinationDomain(resultToAdd);
		}
	});


	#deconstructSearchResultsItem = (item: EventSearchResults) => {
		const { searchResults, ...networkRequestInformation } = item;
		const { originUrl, destinationUrl, eventIndex, codeUrls } = networkRequestInformation;
		const hideDetails = !this.licensePermissions.mayGenerateDetailedEvidenceAt({pageUrl: originUrl});
		const pageDomain = urlToDomain(originUrl);
		const destinationDomain = urlToDomain(destinationUrl);
		const sourceRegisteredDomain = getRegisteredDomain(pageDomain);
		const destinationRegisteredDomain = getRegisteredDomain(destinationDomain);
		return { searchResults, networkRequestInformation, originUrl, destinationUrl, eventIndex, hideDetails, pageDomain, destinationDomain, sourceRegisteredDomain, destinationRegisteredDomain, codeUrls };
	}

	addSearchResultsBySearchTerm = (addition: EventSearchResults) => {
		const { searchResults, networkRequestInformation, eventIndex, hideDetails, sourceRegisteredDomain, destinationRegisteredDomain } = this.#deconstructSearchResultsItem(addition);
		if (sourceRegisteredDomain == null || destinationRegisteredDomain == null) return;
		for (const r of searchResults) {
			const {inferredDataTypeName, searchTermAsSpecified} = r;
			const encodingString = describeEncodedSearchTermUsingFunctionNotationHtml({...r, hideDetails});
			addOrUpdateArrayItemWithFieldName(this._searchResultsBySearchTerm, 'searchTerm', searchTermAsSpecified, 
				() => ({searchTerm: searchTermAsSpecified, inferredTypeNames: observable.map(), destinations: observable.array()}),
				(({ destinations, inferredTypeNames: inferredTypeNamesForSearchTerm }) => {
					addOrUpdateArrayItemWithFieldName(destinations, 'destinationRegisteredDomain', destinationRegisteredDomain,
						() => ({destinationRegisteredDomain, inferredTypeNames: observable.map(), sourcePages: observable.array()}),
						(({ sourcePages, inferredTypeNames: inferredTypeNamesForDestinationRegisteredDomain }) => {
							addOrUpdateArrayItemWithFieldName(sourcePages, 'sourceRegisteredDomain', sourceRegisteredDomain,
								() => ({sourceRegisteredDomain, encodings: observable.array(), inferredTypeNames: observable.map()}),
								(({ encodings, inferredTypeNames: _ }) => {									
									addOrUpdateArrayItemWithFieldName(encodings, 'encodingString', encodingString, () => ({ encodingString, eventsAndResults: observable.map(), inferredTypeNames: observable.map() }),
										(({ eventsAndResults, inferredTypeNames }) => {
											if (inferredDataTypeName != null) {
												mapGetOrCreate(inferredTypeNamesForSearchTerm, inferredDataTypeName, () => observable.set<number>()).add(eventIndex);
												mapGetOrCreate(inferredTypeNamesForDestinationRegisteredDomain, inferredDataTypeName, () => observable.set<number>()).add(eventIndex);
												mapGetOrCreate(inferredTypeNames, inferredDataTypeName, () => observable.set<number>()).add(eventIndex);
											}
											const searchResult: EventSearchResult = {searchResult: r, ...networkRequestInformation};
											eventsAndResults.set(EventSearchResultKey(searchResult), searchResult);
									}));
							}));
					}))
			}));
		}
	}

	deleteSearchResultsBySearchTerm = (addition: EventSearchResults) => {
		const { searchResults, networkRequestInformation, eventIndex, hideDetails, sourceRegisteredDomain, destinationRegisteredDomain } = this.#deconstructSearchResultsItem(addition);
		if (sourceRegisteredDomain == null || destinationRegisteredDomain == null) return;
		for (const r of searchResults) {
			const {inferredDataTypeName, searchTermAsSpecified} = r;
			const encodingString = describeEncodedSearchTermUsingFunctionNotationHtml({...r, hideDetails});
			updateOrDeleteArrayItemWithFieldName(this._searchResultsBySearchTerm, 'searchTerm', searchTermAsSpecified, 
				(({ destinations, inferredTypeNames: inferredTypeNamesForSearchTerm }) => {
					updateOrDeleteArrayItemWithFieldName(destinations, 'destinationRegisteredDomain', destinationRegisteredDomain,
						(({ sourcePages, inferredTypeNames: inferredTypeNamesForDestinationRegisteredDomain }) => {
							updateOrDeleteArrayItemWithFieldName(sourcePages, 'sourceRegisteredDomain', sourceRegisteredDomain,
								(({ encodings, inferredTypeNames: _ }) => {									
										updateOrDeleteArrayItemWithFieldName(encodings, 'encodingString', encodingString,
											(({ eventsAndResults, inferredTypeNames }) => {
												if (inferredDataTypeName != null) {
													deleteSetMemberOrWholeSetIfEmpty(inferredTypeNamesForSearchTerm, inferredDataTypeName, eventIndex);
													deleteSetMemberOrWholeSetIfEmpty(inferredTypeNamesForDestinationRegisteredDomain, inferredDataTypeName, eventIndex);
													deleteSetMemberOrWholeSetIfEmpty(inferredTypeNames, inferredDataTypeName, eventIndex);
												}
												eventsAndResults.delete(EventSearchResultKey({searchResult: r, ...networkRequestInformation}));
												return eventsAndResults.size === 0; // If no events left, delete the encoding
										}));
										return encodings.length === 0; // If no encodings left, delete the source page
								}));
								return sourcePages.length === 0; // If no source pages left, delete the destination
						}))
						return destinations.length === 0; // If no destinations left, delete the search term
					})
			);
		}
	}

	addSearchResultsBySourceRegisteredDomain = (addition: EventSearchResults) => {
		const { searchResults, networkRequestInformation, eventIndex, hideDetails, sourceRegisteredDomain, destinationRegisteredDomain } = this.#deconstructSearchResultsItem(addition);
		if (sourceRegisteredDomain == null || destinationRegisteredDomain == null) return;
		addOrUpdateArrayItemWithFieldName(this._searchResultsBySourceRegisteredDomain, 'sourceRegisteredDomain', sourceRegisteredDomain, 
			() => ({sourceRegisteredDomain, destinations: observable.array()}),
			(({ destinations }) => {
				addOrUpdateArrayItemWithFieldName(destinations, 'destinationRegisteredDomain', destinationRegisteredDomain,
					() => ({destinationRegisteredDomain, searchTerms: observable.array()}),
					(({ searchTerms }) => {
						for (const r of searchResults) {
							const {inferredDataTypeName} = r;
							const encodingString = describeEncodedSearchTermUsingFunctionNotationHtml({...r, hideDetails});
							addOrUpdateArrayItemWithFieldName(searchTerms, 'searchTermAsSpecified', r.searchTermAsSpecified,
								() => ({searchTermAsSpecified: r.searchTermAsSpecified, encodings: observable.array(), inferredTypeNames: observable.map()}),
								(({ encodings, inferredTypeNames: inferredTypeNamessearchTermAsSpecified }) => {
									addOrUpdateArrayItemWithFieldName(encodings, 'encodingString', encodingString,
										() => ({ encodingString, eventsAndResults: observable.map(), inferredTypeNames: observable.map() }),
										(({ eventsAndResults, inferredTypeNames }) => {
											if (inferredDataTypeName != null) {
												mapGetOrCreate(inferredTypeNamessearchTermAsSpecified, inferredDataTypeName, () => observable.set<number>()).add(eventIndex);
												mapGetOrCreate(inferredTypeNames, inferredDataTypeName, () => observable.set<number>()).add(eventIndex);
											}
											const searchResult: EventSearchResult = {searchResult: r, ...networkRequestInformation};
											eventsAndResults.set(EventSearchResultKey(searchResult), searchResult);
									}));
							}));
						}
				}));
		}));
	};

	deleteSearchResultsBySourceRegisteredDomain = (itemToRemove: EventSearchResults) => {
		const { searchResults, networkRequestInformation, eventIndex, hideDetails, sourceRegisteredDomain, destinationRegisteredDomain } = this.#deconstructSearchResultsItem(itemToRemove);
		if (sourceRegisteredDomain == null || destinationRegisteredDomain == null) return;
		updateOrDeleteArrayItemWithFieldName(this._searchResultsBySourceRegisteredDomain, 'sourceRegisteredDomain', sourceRegisteredDomain, 
			(({ destinations }) => {
				updateOrDeleteArrayItemWithFieldName(destinations, 'destinationRegisteredDomain', destinationRegisteredDomain,
					(({ searchTerms }) => {
						for (const r of searchResults) {
							const {inferredDataTypeName} = r;
							const encodingString = describeEncodedSearchTermUsingFunctionNotationHtml({...r, hideDetails});
							updateOrDeleteArrayItemWithFieldName(searchTerms, 'searchTermAsSpecified', r.searchTermAsSpecified,
								(({ encodings, inferredTypeNames: inferredTypeNamessearchTermAsSpecified }) => {
									updateOrDeleteArrayItemWithFieldName(encodings, 'encodingString', encodingString,
										(({ eventsAndResults, inferredTypeNames }) => {
											if (inferredDataTypeName != null) {
												deleteSetMemberOrWholeSetIfEmpty(inferredTypeNamessearchTermAsSpecified, inferredDataTypeName, eventIndex);
												deleteSetMemberOrWholeSetIfEmpty(inferredTypeNames, inferredDataTypeName, eventIndex);
											}
											eventsAndResults.delete(EventSearchResultKey({searchResult: r, ...networkRequestInformation}));
											return eventsAndResults.size === 0; // If no events left, delete the encoding
									}));
									return encodings.length === 0; // If no encodings left, delete the search term
							}));
						}
						return searchTerms.length === 0; // If no search terms left, delete the destination
				}));
				return destinations.length === 0; // If no destinations left, delete the source registered domain
		}));
	};

	addSearchResultsByDestinationDomain = action((addition: EventSearchResults) => {
		const { searchResults, networkRequestInformation, eventIndex, hideDetails, sourceRegisteredDomain, destinationRegisteredDomain } = this.#deconstructSearchResultsItem(addition);
		if (sourceRegisteredDomain == null || destinationRegisteredDomain == null) return;
		addOrUpdateArrayItemWithFieldName(this.searchResultsByDestinationDomain, 'destinationRegisteredDomain', destinationRegisteredDomain, 
			() => ({destinationRegisteredDomain, sources: observable.array(), inferredTypeNames: observable.map()}),
			(({ sources }) => {
				addOrUpdateArrayItemWithFieldName(sources, 'sourceRegisteredDomain', sourceRegisteredDomain,
					() => ({sourceRegisteredDomain, searchTerms: observable.array(), inferredTypeNames: observable.map()}),
					(({ searchTerms }) => {
						for (const r of searchResults) {
							const {inferredDataTypeName} = r;
							const encodingString = describeEncodedSearchTermUsingFunctionNotationHtml({...r, hideDetails});
							addOrUpdateArrayItemWithFieldName(searchTerms, 'searchTermAsSpecified', r.searchTermAsSpecified,
								() => ({searchTermAsSpecified: r.searchTermAsSpecified, encodings: observable.array(), inferredTypeNames: observable.map()}),
								(({ encodings, inferredTypeNames: inferredTypeNamessearchTermAsSpecified }) => {
									addOrUpdateArrayItemWithFieldName(encodings, 'encodingString', encodingString,
										() => ({ encodingString, eventsAndResults: observable.map(), inferredTypeNames: observable.map() }),
										(({ eventsAndResults, inferredTypeNames }) => {
											if (inferredDataTypeName != null) {
												mapGetOrCreate(inferredTypeNamessearchTermAsSpecified, inferredDataTypeName, () => observable.set<number>()).add(eventIndex);
												mapGetOrCreate(inferredTypeNames, inferredDataTypeName, () => observable.set<number>()).add(eventIndex);
											}
											const searchResult: EventSearchResult = {searchResult: r, ...networkRequestInformation};
											eventsAndResults.set(EventSearchResultKey(searchResult), searchResult);
									}));
							}));
						}
				}));
		}));
	});

	deleteSearchResultsByDestinationDomain = action((itemToRemove: EventSearchResults) => {
		const { searchResults, networkRequestInformation, eventIndex, hideDetails, sourceRegisteredDomain, destinationRegisteredDomain } = this.#deconstructSearchResultsItem(itemToRemove);
		if (sourceRegisteredDomain == null || destinationRegisteredDomain == null) return;
		updateOrDeleteArrayItemWithFieldName(this.searchResultsByDestinationDomain, 'destinationRegisteredDomain', destinationRegisteredDomain, 
			(({ sources }) => {
				updateOrDeleteArrayItemWithFieldName(sources, 'sourceRegisteredDomain', sourceRegisteredDomain,
					(({ searchTerms }) => {
						for (const r of searchResults) {
							const {inferredDataTypeName} = r;
							const encodingString = describeEncodedSearchTermUsingFunctionNotationHtml({...r, hideDetails});
							updateOrDeleteArrayItemWithFieldName(searchTerms, 'searchTermAsSpecified', r.searchTermAsSpecified,
								(({ encodings, inferredTypeNames: inferredTypeNamessearchTermAsSpecified }) => {
									updateOrDeleteArrayItemWithFieldName(encodings, 'encodingString', encodingString,
										(({ eventsAndResults, inferredTypeNames }) => {
											if (inferredDataTypeName != null) {
												deleteSetMemberOrWholeSetIfEmpty(inferredTypeNamessearchTermAsSpecified, inferredDataTypeName, eventIndex);
												deleteSetMemberOrWholeSetIfEmpty(inferredTypeNames, inferredDataTypeName, eventIndex);
											}
											eventsAndResults.delete(EventSearchResultKey({searchResult: r, ...networkRequestInformation}));
											return eventsAndResults.size === 0; // If no events left, delete the encoding
									}));
									return encodings.length === 0; // If no encodings left, delete the search term
							}));
						}
						return searchTerms.length === 0; // If no search terms left, delete the source
				}));
				return sources.length === 0; // If no sources left, delete the destination domain
		}));
	});

	addByPageDomainAndCodeUrlAndDestinationDomain =  (addition: EventSearchResults) => {
		const { searchResults, networkRequestInformation, eventIndex, hideDetails, pageDomain, destinationDomain, codeUrls } = this.#deconstructSearchResultsItem(addition);
		if (pageDomain == null || destinationDomain == null || codeUrls == null) return;
		for (const searchResult of searchResults) {
			const { searchTermAsSpecified, direction, inferredDataTypeName } = searchResult;
			if (direction !== 'sent') return;
			const encodingString = describeEncodedSearchTermUsingFunctionNotationHtml({...searchResult, hideDetails});

			const forPageDomain = mapGetOrCreate(this._byPageDomainAndCodeUrlAndDestinationDomain, pageDomain, () => ({pageDomain, byCodeUrl: observable.map(), inferredTypeNames: observable.map()}));
			for (const codeUrl of codeUrls) {
				const byCodeUrl = mapGetOrCreate(forPageDomain.byCodeUrl, codeUrl, () => ({ codeUrl, byDestinationDomain: observable.array(), inferredTypeNames: observable.map() }));
				addOrUpdateArrayItemWithFieldName(byCodeUrl.byDestinationDomain, 'destinationDomain', destinationDomain,
					() => ({ destinationDomain, bySearchTerm: observable.array(), inferredTypeNames: observable.map() }),
					({bySearchTerm, inferredTypeNames: inferredTypeNamesForDestinationDomain}) => {
						addOrUpdateArrayItemWithFieldName(bySearchTerm, 'searchTermAsSpecified', searchTermAsSpecified,
							() => ({ searchTermAsSpecified, encodings: observable.array(), inferredTypeNames: observable.map() }),
							({ encodings, inferredTypeNames: inferredTypeNamesForSearchTerm }) => {
								addOrUpdateArrayItemWithFieldName(encodings, 'encodingString', encodingString,
									() => ({ encodingString, eventsAndResults: observable.map(), inferredTypeNames: observable.map() }),
									({ eventsAndResults, inferredTypeNames }) => {
										if (inferredDataTypeName != null) {
											mapGetOrCreate(inferredTypeNamesForSearchTerm, inferredDataTypeName, () => observable.set<number>()).add(eventIndex);
											mapGetOrCreate(inferredTypeNamesForDestinationDomain, inferredDataTypeName, () => observable.set<number>()).add(eventIndex);
											mapGetOrCreate(inferredTypeNames, inferredDataTypeName, () => observable.set<number>()).add(eventIndex);
										}										
										const eventSearchResult: EventSearchResult = {searchResult, ...networkRequestInformation};
										eventsAndResults.set(EventSearchResultKey(eventSearchResult), eventSearchResult);
								});
						});
				});
			}
		}
	}

	deleteByPageDomainAndCodeUrlAndDestinationDomain =  (itemToRemove: EventSearchResults) => {
		const { searchResults, networkRequestInformation, eventIndex, hideDetails, pageDomain, destinationDomain, codeUrls } = this.#deconstructSearchResultsItem(itemToRemove);
		if (pageDomain == null || destinationDomain == null || codeUrls == null) return;
		for (const searchResult of searchResults) {
			const { searchTermAsSpecified, direction, inferredDataTypeName } = searchResult;
			if (direction !== 'sent') return;
			const forPageDomain = this._byPageDomainAndCodeUrlAndDestinationDomain.get(pageDomain);
			if (forPageDomain == null) return;
			const encodingString = describeEncodedSearchTermUsingFunctionNotationHtml({...searchResult, hideDetails});

			for (const codeUrl of codeUrls) {
				const byCodeUrl = mapGetOrCreate(forPageDomain.byCodeUrl, codeUrl, () => ({ codeUrl, byDestinationDomain: observable.array(), inferredTypeNames: observable.map() }));
				updateOrDeleteArrayItemWithFieldName(byCodeUrl.byDestinationDomain, 'destinationDomain', destinationDomain,
					({bySearchTerm, inferredTypeNames: inferredTypeNamesForDestinationDomain}) => {
						updateOrDeleteArrayItemWithFieldName(bySearchTerm, 'searchTermAsSpecified', searchTermAsSpecified,
							({ encodings, inferredTypeNames: inferredTypeNamesForSearchTerm }) => {
								updateOrDeleteArrayItemWithFieldName(encodings, 'encodingString', encodingString,
									({ eventsAndResults, inferredTypeNames }) => {
										if (inferredDataTypeName != null) {
											deleteSetMemberOrWholeSetIfEmpty(inferredTypeNamesForSearchTerm, inferredDataTypeName, eventIndex);
											deleteSetMemberOrWholeSetIfEmpty(inferredTypeNamesForDestinationDomain, inferredDataTypeName, eventIndex);
											deleteSetMemberOrWholeSetIfEmpty(inferredTypeNames, inferredDataTypeName, eventIndex);
										}
										eventsAndResults.delete(EventSearchResultKey({searchResult, ...networkRequestInformation}));
										return eventsAndResults.size === 0; // If no events left, delete the encoding
								});
								return encodings.length === 0; // If no encodings left, delete the search term
						});
						return bySearchTerm.length === 0; // If no search terms left, delete the destination domain
				});
			}
		}
	}

}
